import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Mail, Phone, MapPin, Send, Download, Calendar, Users, DollarSign } from 'lucide-react';
import './Contact.css';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    message: '',
    interest: 'investment'
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Handle form submission
    console.log('Form submitted:', formData);
  };

  const investmentOptions = [
    {
      type: "Seed Funding",
      amount: "$500K - $1M",
      equity: "10-15%",
      use: "Product development, team expansion, market validation",
      timeline: "Immediate"
    },
    {
      type: "Series A",
      amount: "$2M - $5M",
      equity: "15-20%",
      use: "Scaling operations, international expansion, advanced AI development",
      timeline: "12-18 months"
    },
    {
      type: "Strategic Partnership",
      amount: "Variable",
      equity: "Negotiable",
      use: "Joint ventures, distribution partnerships, technology integration",
      timeline: "Flexible"
    }
  ];

  return (
    <section className="contact" id="contact">
      <div className="container">
        <motion.div
          className="section-header"
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 1 }}
          viewport={{ once: true }}
        >
          <h2 className="section-title">Let's Build the Future Together</h2>
          <p className="section-subtitle">
            Ready to invest in the future of fitness? We'd love to hear from you.
          </p>
        </motion.div>

        <div className="contact-content">
          <motion.div
            className="contact-info"
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 1, delay: 0.2 }}
            viewport={{ once: true }}
          >
            <h3>Investment Opportunities</h3>
            <p>
              We're seeking strategic partners who share our vision of democratizing 
              fitness through technology and community. Join us in building the 
              world's leading fitness platform.
            </p>

            <div className="investment-options">
              {investmentOptions.map((option, index) => (
                <motion.div
                  key={index}
                  className="investment-option"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  whileHover={{ y: -5, scale: 1.02 }}
                >
                  <div className="option-header">
                    <h4>{option.type}</h4>
                    <span className="amount">{option.amount}</span>
                  </div>
                  <div className="option-details">
                    <div className="detail">
                      <span className="label">Equity:</span>
                      <span className="value">{option.equity}</span>
                    </div>
                    <div className="detail">
                      <span className="label">Use of Funds:</span>
                      <span className="value">{option.use}</span>
                    </div>
                    <div className="detail">
                      <span className="label">Timeline:</span>
                      <span className="value">{option.timeline}</span>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>


          </motion.div>

          <motion.div
            className="contact-form"
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 1, delay: 0.4 }}
            viewport={{ once: true }}
          >
            <h3>Get in Touch</h3>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label htmlFor="name">Full Name *</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="email">Email Address *</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="company">Company</label>
                <input
                  type="text"
                  id="company"
                  name="company"
                  value={formData.company}
                  onChange={handleChange}
                />
              </div>

              <div className="form-group">
                <label htmlFor="interest">Interest *</label>
                <select
                  id="interest"
                  name="interest"
                  value={formData.interest}
                  onChange={handleChange}
                  required
                >
                  <option value="investment">Investment</option>
                  <option value="partnership">Partnership</option>
                  <option value="acquisition">Acquisition</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="message">Message *</label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows="5"
                  required
                  placeholder="Tell us about your interest in FitHive..."
                />
              </div>

              <motion.button
                type="submit"
                className="btn btn-primary submit-btn"
                whileHover={{ scale: 1.05, y: -2 }}
                whileTap={{ scale: 0.95 }}
              >
                <Send size={20} />
                Send Message
              </motion.button>
            </form>
          </motion.div>
        </div>

        <motion.div
          className="resources-section"
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.6 }}
          viewport={{ once: true }}
        >
          <h3>Additional Resources</h3>
          <div className="resources-grid">
            <motion.div
              className="resource-card"
              whileHover={{ y: -5, scale: 1.02 }}
            >
              <Download size={32} />
              <h4>Pitch Deck</h4>
              <p>Access our comprehensive investor presentation and business plan</p>
              <a 
                href="https://hackmd.io/@AQaddora/rJapaCuggx" 
                target="_blank" 
                rel="noopener noreferrer"
                className="btn btn-secondary"
              >
                View Documentation
              </a>
            </motion.div>

            <motion.div
              className="resource-card"
              whileHover={{ y: -5, scale: 1.02 }}
            >
              <Calendar size={32} />
              <h4>Investment Details</h4>
              <p>Learn about our funding rounds and investment opportunities</p>
              <a 
                href="https://hackmd.io/@AQaddora/rJapaCuggx" 
                target="_blank" 
                rel="noopener noreferrer"
                className="btn btn-secondary"
              >
                View Documentation
              </a>
            </motion.div>

            <motion.div
              className="resource-card"
              whileHover={{ y: -5, scale: 1.02 }}
            >
              <Users size={32} />
              <h4>Company Overview</h4>
              <p>Explore our team, vision, and company structure</p>
              <a 
                href="https://hackmd.io/@AQaddora/rJapaCuggx" 
                target="_blank" 
                rel="noopener noreferrer"
                className="btn btn-secondary"
              >
                View Documentation
              </a>
            </motion.div>

            <motion.div
              className="resource-card"
              whileHover={{ y: -5, scale: 1.02 }}
            >
              <DollarSign size={32} />
              <h4>Financial Data</h4>
              <p>Review our financial projections and market analysis</p>
              <a 
                href="https://hackmd.io/@AQaddora/rJapaCuggx" 
                target="_blank" 
                rel="noopener noreferrer"
                className="btn btn-secondary"
              >
                View Documentation
              </a>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Contact;
